// Which way a rubric's levels run, read from their names alone: worst-to-best
// (true), best-to-worst (false), or null when the names cannot tell. Both
// conventions are in live use ("Below Standard .. Exceeding Standard" next to
// "Band 6 .. Band 1"), so array order by itself never says which end is the top.
//
// Port of feedbacksBackend RubricLadderDirection (shared there by MarkResolver
// and AiFeedbackDiff). The rubric → mark path is pinned to it by the shared LTI
// golden fixtures; keep the vocabulary and the rules identical.

const BASE_QUALITY_VOCABULARY = [
  ['well above standard', 95],
  ['well above', 95],
  ['moving further', 95],
  ['outstanding', 95],
  ['exceptional', 95],
  ['excellence', 95],
  ['excellent', 95],
  ['exceeding', 95],
  ['exceeds', 95],
  ['very high', 90],
  ['sophisticated', 90],
  ['perceptive', 90],
  ['insightful', 90],
  ['convincing', 90],
  ['expert', 90],
  ['skilful', 85],
  ['skillful', 85],
  ['extensive', 85],
  ['extending', 85],
  ['comprehensive', 80],
  ['very good', 80],
  ['sustained', 80],
  ['above the standard', 70],
  ['above standard', 70],
  ['above expected', 70],
  ['above', 70],
  ['proficient', 70],
  ['advancing', 70],
  ['deepening', 70],
  ['effective', 70],
  ['thorough', 70],
  ['strong', 70],
  ['merit', 70],
  ['high', 70],
  ['competent', 65],
  ['good', 65],
  ['focused', 60],
  ['clear', 60],
  ['at the expected standard', 50],
  ['at expected', 50],
  ['at standard', 50],
  ['demonstrating', 50],
  ['satisfactory', 50],
  ['sufficient', 50],
  ['moderate', 50],
  ['achieved', 50],
  ['maturing', 50],
  ['average', 50],
  ['medium', 50],
  ['middle', 50],
  ['sound', 50],
  ['intermediate', 35],
  ['adequate', 35],
  ['working within', 30],
  ['towards expected', 30],
  ['approaching', 30],
  ['developing', 30],
  ['towards', 30],
  ['minimal', 30],
  ['some', 30],
  ['below the standard', 25],
  ['below standard', 25],
  ['below expected', 25],
  ['below', 25],
  ['limited', 25],
  ['partial', 25],
  ['low', 25],
  ['unsatisfactory', 20],
  ['ineffective', 20],
  ['inadequate', 20],
  ['little', 20],
  ['basic', 20],
  ['insufficient', 15],
  ['working towards', 15],
  ['beginning', 15],
  ['emerging', 15],
  ['very low', 15],
  ['novice', 15],
  ['well below standard', 10],
  ['well below', 10],
  ['elementary', 10],
  ['not demonstrated', 5],
  ['not attempted', 5],
  ['not achieved', 5],
  ['not evident', 5],
  ['no evidence', 5],
];

// Longest phrase first (stable for ties) so "not achieved" beats "achieved" and
// "very high" beats "high".
const QUALITY_VOCABULARY = [...BASE_QUALITY_VOCABULARY].sort(
  (left, right) => right[0].length - left[0].length
);

// Marks read more wording than AiFeedbackDiff does: these phrases come from a
// 2026-10 survey of published scales (AU, NZ, UK, US, CA, INT) and are used by
// meaningScores only, so the teacher-edit data profiles keep reading ladders
// exactly as before.
const MARK_ONLY_VOCABULARY = [
  // frequency
  ['always', 95],
  ['consistently', 90],
  ['mostly', 75],
  ['usually', 70],
  ['frequently', 70],
  ['frequent', 70],
  ['often', 65],
  ['sometimes', 40],
  ['occasionally', 35],
  ['seldom', 20],
  ['rarely', 20],
  ['never', 5],
  // amounts, usually qualifying support / errors (meaningScore flips those)
  ['considerable', 75],
  ['significant', 75],
  ['additional', 60],
  ['few', 25],
  ['no', 0],
  // outcomes
  ['high distinction', 95],
  ['distinction', 85],
  ['credit', 70],
  ['pass', 55],
  ['fail', 10],
  ['exemplary', 95],
  ['mastery', 95],
  ['mastered', 95],
  ['greater depth', 90],
  ['advanced', 90],
  ['fully met', 90],
  ['fully', 90],
  ['yes', 90],
  ['complete', 80],
  ['secure', 60],
  ['meets', 60],
  ['met', 60],
  ['expected', 50],
  ['partial mastery', 40],
  ['partially met', 40],
  ['partially meets', 40],
  ['partially', 40],
  ['fair', 40],
  ['approaches', 30],
  ['weak', 25],
  ['concerns', 25],
  ['advanced beginner', 25],
  ['needs improvement', 20],
  ['needs work', 20],
  ['not yet competent', 20],
  ['not competent', 15],
  ['not satisfactory', 15],
  ['does not meet', 15],
  ['not meet', 15],
  ['not met', 15],
  ['incomplete', 15],
  ['poor', 15],
  ['below basic', 10],
  ['not yet', 10],
  ['far below', 5],
  // taxonomies and frameworks (Bloom, SOLO, Dreyfus, AAC&U VALUE, AITSL, WIDA)
  ['create', 85],
  ['evaluate', 70],
  ['analyse', 55],
  ['analyze', 55],
  ['apply', 40],
  ['understand', 25],
  ['remember', 10],
  ['extended abstract', 90],
  ['relational', 70],
  ['multistructural', 45],
  ['unistructural', 25],
  ['prestructural', 5],
  ['practitioner', 65],
  ['apprentice', 35],
  ['capstone', 95],
  ['milestones', 50],
  ['milestone', 50],
  ['benchmark', 15],
  ['lead', 90],
  ['highly accomplished', 80],
  ['graduate', 20],
  ['reaching', 85],
  ['bridging', 65],
  ['expanding', 50],
  ['entering', 5],
  // more outcome wording
  ['full marks', 95],
  ['no marks', 0],
  ['all', 95],
  ['most', 75],
  ['very few', 15],
  ['none', 5],
  ['almost always', 85],
  ['gist', 45],
  ['isolated', 15],
  ['highly evident', 90],
  ['evident', 65],
  ['moderately evident', 45],
  ['slightly evident', 25],
  ['above satisfactory', 75],
  ['below satisfactory', 25],
  ['acceptable', 55],
  ['unacceptable', 15],
  ['first class', 95],
  ['first', 90],
  ['upper second', 75],
  ['lower second', 60],
  ['third class', 45],
  ['third', 45],
  ['applying', 95],
  ['making connections', 75],
  ['working with', 55],
  ['exploring', 35],
  ['becoming aware', 15],
  ['meeting', 60],
  ['nearly meets', 40],
  ['did not meet', 10],
  ['above average', 75],
  ['below average', 25],
  ['very satisfactory', 70],
  ['fairly satisfactory', 40],
  ['requires improvement', 20],
  ['very poor', 5],
  ['highly developed', 90],
  ['well developed', 85],
  ['developed', 65],
  ['underdeveloped', 20],
  ['undeveloped', 15],
  ['mostly clear', 50],
  ['unclear', 20],
  ['nuanced', 90],
  ['adequately', 60],
  ['minimally', 20],
  ['lacking', 20],
  ['lacks', 20],
  ['not at all', 5],
  // Likert
  ['strongly agree', 95],
  ['neither agree nor disagree', 50],
  ['agree', 75],
  ['neutral', 50],
  ['strongly disagree', 5],
  ['disagree', 25],
  // can / cannot
  ['cannot', 10],
  ['unable', 10],
  ['does not', 10],
  // more stage and descriptor wording
  ['no credit', 10],
  ['present', 70],
  ['securing', 60],
  ['mastering', 90],
  ['presented', 30],
  ['practising', 55],
  ['practicing', 55],
  ['fluent', 85],
  ['efficient', 85],
  ['precise', 90],
  ['justified', 85],
  ['reliable', 70],
  ['appropriate', 65],
  ['relevant', 60],
  ['workable', 55],
  ['informal', 35],
  ['misused', 15],
  ['guesses', 10],
  ['unsafe', 10],
  // Dutch
  ['uitstekend', 95],
  ['zeer goed', 85],
  ['goed', 75],
  ['ruim voldoende', 65],
  ['voldoende', 55],
  ['onvoldoende', 15],
  // Italian
  ['ottimo', 95],
  ['eccellente', 95],
  ['distinto', 80],
  ['buono', 75],
  ['discreto', 65],
  ['sufficiente', 55],
  ['insufficiente', 15],
  // Portuguese
  ['muito bom', 85],
  ['bom', 75],
  ['suficiente', 55],
  // Spanish (more)
  ['sobresaliente', 95],
  ['notable', 80],
  // Indonesian
  ['sangat baik', 95],
  ['baik', 75],
  ['cukup', 55],
  ['sangat kurang', 10],
  ['kurang', 25],
  // Vietnamese (accents stripped)
  ['xuat sac', 95],
  ['gioi', 85],
  ['kha', 70],
  ['trung binh', 50],
  ['yeu', 25],
  ['kem', 10],
  // German (erreicht) and French (acquis / maitrise)
  ['ubertroffen', 95],
  ['nicht erreicht', 10],
  ['teilweise erreicht', 40],
  ['erreicht', 70],
  ['depasse', 95],
  ['non acquis', 10],
  ['en cours d acquisition', 40],
  ['acquis', 75],
  ['tres bonne maitrise', 95],
  ['maitrise satisfaisante', 70],
  ['maitrise fragile', 35],
  ['maitrise insuffisante', 10],
  ['full', 90],
  ['substantially', 75],
  ['substantial', 75],
  ['working beyond', 90],
  ['beyond', 90],
  ['working at', 50],
  ['nailed it', 95],
  ['got it', 90],
  ['almost there', 60],
  ['almost', 60],
  ['correct', 90],
  ['incorrect', 10],
  ['accurate', 85],
  ['inaccurate', 15],
  ['completed', 90],
  ['in progress', 50],
  ['not started', 5],
  ['attempted', 30],
  ['missing', 5],
  ['lots', 80],
  ['major errors', 20],
  ['major error', 20],
  ['minor errors', 70],
  ['minor error', 70],
  ['excelling', 95],
  ['achieving', 60],
  ['struggling', 10],
  ['consistent', 85],
  ['inconsistent', 25],
  ['extends', 95],
  ['complex', 75],
  ['simple', 25],
  ['in depth', 90],
  ['detailed', 75],
  ['superficial', 20],
  ['partly', 40],
  ['little or no', 5],
  ['little to no', 5],
  ['platinum', 95],
  ['gold', 90],
  ['silver', 60],
  ['bronze', 30],
  ['spicy', 85],
  ['hot', 85],
  ['mild', 30],
  ['excelente', 95],
  ['bueno', 75],
  ['regular', 50],
  ['insuficiente', 15],
  ['tres bien', 95],
  ['assez bien', 60],
  ['bien', 75],
  ['insuffisant', 15],
  // traffic lights
  ['green', 85],
  ['amber', 50],
  ['red', 15],
  // student self-assessment
  ['teach others', 95],
  ['teach this', 95],
  ['independently', 75],
  ['independent', 75],
  ['by myself', 75],
  ['with support', 35],
  ['with help', 35],
  ['direct instruction', 15],
  ['can t', 10],
  // te reo Maori (NCEA) and German grades, macrons and umlauts stripped
  ['kairangi', 95],
  ['kaiaka', 70],
  ['paetae', 50],
  ['kaore ano kia paetae', 5],
  ['kaore i paetae', 5],
  ['sehr gut', 95],
  ['gut', 75],
  ['befriedigend', 60],
  ['ausreichend', 45],
  ['mangelhaft', 25],
  ['ungenugend', 5],
];

const MARK_VOCABULARY = [
  ...BASE_QUALITY_VOCABULARY,
  ...MARK_ONLY_VOCABULARY,
].sort((left, right) => right[0].length - left[0].length);

// Wording where MORE of the named thing means a WORSE outcome ("Limited support
// required" outranks "Extensive support required"): the vocabulary would read
// such a ladder backwards, so it gives no direction instead.
const INVERTED_POLARITY_NOUNS = [
  'support',
  'assistance',
  'intervention',
  'scaffolding',
  'prompting',
  'guidance',
  'help',
  'supervision',
  'reminders',
  'error',
  'errors',
  'mistake',
  'mistakes',
  'omission',
  'omissions',
  'risk',
  'risks',
  'severity',
  'dependence',
  'dependency',
  'difficulty',
  'difficulties',
  'weakness',
  'weaknesses',
  'lapse',
  'lapses',
  'gap',
  'gaps',
];

// Lower-cased, punctuation-collapsed and space-padded so phrases match on word
// boundaries; null for a name with no words.
// Accents are stripped first (NFD, then combining marks U+0300-U+036F) so
// "Kāore" reads as "kaore" and "ungenügend" as "ungenugend".
const normalize = (name) => {
  const cleaned = stableLowerCase(
    String(name ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  )
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return cleaned ? ` ${cleaned} ` : null;
};

// A name's words with accents stripped and Latin, Greek and Cyrillic
// lower-cased: "Non noté" → "non note".
export const plainWords = (name) => (normalize(name) || '').trim();

// Lower-cases U+0000-U+052F only (Latin, Greek, Cyrillic): their case mappings
// are the same in every JS engine and Java version, while newer scripts' differ
// between Unicode versions and would let JS and Java read a name differently.
export const stableLowerCase = (text) =>
  text.replace(/[\u0000-\u052f]/g, (char) => char.toLowerCase());

// Java String.trim(): strips only chars <= U+0020, unlike JS trim(). Used
// wherever a trimmed name decides a match, so JS and Java read it the same.
export const javaTrim = (text) =>
  text.replace(/^[\u0000-\u0020]+|[\u0000-\u0020]+$/g, '');

// The first number in a name, decimals included: progression codes such as
// "1.1 .. 1.5" share their whole-number part and only the decimals order them.
// A minus counts only when it starts the name ("-3"): inside one it is a dash
// ("Band-6", "1-2").
const firstNumber = (name) => {
  const match = String(name ?? '').match(
    /^[ \t\n\v\f\r]*(-\d+(?:\.\d+)?)|\d+(?:\.\d+)?/
  );
  if (!match) {
    return null;
  }
  const value = Number(match[1] === undefined ? match[0] : match[1]);
  // "-0" is 0: JS compares -0 and 0 as equal, Java's Double.compare does not.
  return value === 0 ? 0 : value;
};

const letterOrdinal = (name) => {
  const trimmed = javaTrim(String(name ?? '')).toUpperCase();
  return trimmed.length === 1 && trimmed >= 'A' && trimmed <= 'Z'
    ? trimmed.charCodeAt(0) - 65
    : null;
};

// Weakly monotonic in the given direction with at least one strict step: real
// ladders sub-divide a band ("4A" then "4P"), but a run of equal values alone is
// not a ladder.
const monotonic = (values, increasing) => {
  let sawStrictStep = false;
  for (let i = 1; i < values.length; i += 1) {
    // Compared, not subtracted: two huge numbers both parse to Infinity, and
    // Infinity - Infinity is NaN (Java's Double.compare calls them equal).
    const previous = values[i - 1];
    const current = values[i];
    if (increasing ? current < previous : current > previous) {
      return false;
    }
    if (current !== previous) {
      sawStrictStep = true;
    }
  }
  return sawStrictStep;
};

// true when values rise along the array in the "better" direction, false when
// they fall, null when neither.
const ordering = (values, higherIsBetter) => {
  if (values.length < 2) {
    return null;
  }
  if (monotonic(values, higherIsBetter)) {
    return true;
  }
  return monotonic(values, !higherIsBetter) ? false : null;
};

export const hasInvertedPolarityWording = (levelNames) =>
  levelNames.some((name) => {
    const normalized = normalize(name);
    return (
      normalized !== null &&
      INVERTED_POLARITY_NOUNS.some((noun) => normalized.includes(` ${noun} `))
    );
  });

const repeats = (values) => new Set(values).size < values.length;

// Repeated numbers (or letters) mean the names carry part of the order, so the
// wording guard applies: "High quality (1 error) .. Low quality (3+ errors)"
// must not read as worst-to-best off the error COUNTS.
const guardedOrdering = (levelNames, values, higherIsBetter) =>
  repeats(values) && hasInvertedPolarityWording(levelNames)
    ? null
    : ordering(values, higherIsBetter);

// Where a level name sits on the quality ladder, higher = better; null when it
// carries no recognisable quality wording. Whole words only: a bare includes
// would read "Unclear" as "clear" and "Highlight" as "high".
export const qualityScore = (levelName) => {
  const normalized = normalize(levelName);
  if (normalized === null) {
    return null;
  }
  const word = QUALITY_VOCABULARY.find(([phrase]) =>
    normalized.includes(` ${phrase} `)
  );
  return word ? word[1] : null;
};

// Nouns that flip the sense of an amount in front of them, for marks: the
// shared list plus wording AiFeedbackDiff never needed.
const MARK_INVERTED_NOUNS = [
  ...INVERTED_POLARITY_NOUNS,
  'improvement',
  'practice',
  'revision',
  'revisions',
  'inaccuracy',
  'inaccuracies',
  'problems',
  'issues',
  'redirection',
  'correction',
  'corrections',
  'prompts',
  'reteaching',
];

// Words that may sit between an amount and its noun: "never makes errors",
// "lots of help", "extensive teacher support". Kept short on purpose: in
// "consistently explains support for a line of reasoning" the support is the
// student's, not help they need.
const NOUN_FILLERS = [
  'makes',
  'make',
  'made',
  'of',
  'with',
  'teacher',
  'adult',
  'peer',
];

// True when the phrase qualifies a more-is-worse noun in this name, directly
// or across one filler word ("limited support", "never makes errors").
export const qualifiesMoreIsWorseNoun = (levelName, phrase) => {
  const normalized = normalize(levelName);
  if (normalized === null) {
    return false;
  }
  return MARK_INVERTED_NOUNS.some(
    (noun) =>
      normalized.includes(` ${phrase} ${noun} `) ||
      NOUN_FILLERS.some((filler) =>
        normalized.includes(` ${phrase} ${filler} ${noun} `)
      )
  );
};

// Words that change the strength of the word after them, and how: a
// multiplier, or 'boost' (half-way to 100).
const MODIFIERS = {
  not: 0.2,
  non: 0.2,
  never: 0.2,
  hardly: 0.2,
  slightly: 0.3,
  barely: 0.3,
  minimally: 0.3,
  rarely: 0.3,
  seldom: 0.3,
  partially: 0.6,
  partly: 0.6,
  somewhat: 0.6,
  sometimes: 0.6,
  inconsistently: 0.6,
  occasionally: 0.6,
  approaching: 0.6,
  mostly: 0.85,
  generally: 0.85,
  usually: 0.85,
  largely: 0.85,
  almost: 0.85,
  nearly: 0.85,
  reasonably: 0.85,
  highly: 'boost',
  very: 'boost',
  fully: 'boost',
  extremely: 'boost',
  exceptionally: 'boost',
  consistently: 'boost',
  always: 'boost',
  completely: 'boost',
  thoroughly: 'boost',
  strongly: 'boost',
  particularly: 'boost',
};

// The part of a name before its first separator (" - ", ":", ";", "("), or
// null when there is none. Whitespace spelled out as ASCII for Java parity.
const LABEL_SEPARATOR = /[ \t\n\v\f\r][-–—][ \t\n\v\f\r]|[:;(]/;
const labelOf = (name) => {
  const text = String(name ?? '');
  const match = text.match(LABEL_SEPARATOR);
  return match && match.index > 0 ? text.slice(0, match.index) : null;
};

// The words before the first occurrence of a phrase in a normalized name.
const wordsBefore = (normalized, phrase) =>
  normalized
    .slice(0, normalized.indexOf(` ${phrase} `))
    .trim()
    .split(' ');

// A strengthener pushes a word further toward its own end ("Very high" up,
// "Very limited" down); negation and weakening only pull a positive word down
// ("Not proficient", "Partially correct").
const modified = (normalized, phrase, score) => {
  const before = wordsBefore(normalized, phrase);
  const previous = before[before.length - 1];
  const modifier =
    previous === 'yet' && before[before.length - 2] === 'not'
      ? 0.3
      : Object.prototype.hasOwnProperty.call(MODIFIERS, previous)
      ? MODIFIERS[previous]
      : null;
  if (modifier === null) {
    return score;
  }
  if (modifier === 'boost') {
    return score >= 50 ? score + (100 - score) / 2 : score / 2;
  }
  return score >= 50 ? score * modifier : score;
};

// True when the word right before the phrase changes it ("Not proficient",
// "Highly effective", "Below basic"): such a name is not that grade.
export const isQualifiedPhrase = (levelName, phrase) => {
  const normalized = normalize(levelName);
  if (normalized === null || !normalized.includes(` ${phrase} `)) {
    return false;
  }
  const before = wordsBefore(normalized, phrase);
  const previous = before[before.length - 1];
  return (
    Object.prototype.hasOwnProperty.call(MODIFIERS, previous) ||
    ['below', 'above', 'well', 'towards', 'yet'].includes(previous)
  );
};

const isModifierPhrase = (phrase) =>
  Object.prototype.hasOwnProperty.call(MODIFIERS, phrase);

// What each level of one criterion means (0-100), read from its quality
// wording:
// - the longest phrase in the level, preferring the word being described over
//   a modifier ("proficient" in "Partially proficient"), and skipping phrases
//   every level shares ("developed" in "Fully developed .. Minimally
//   developed") as long as each level has another;
// - adjusted by the word before it ("Not proficient" low, "Highly proficient"
//   high);
// - read the other way when it is an amount of a more-is-worse thing
//   ("Extensive support" low, "Few errors" high).
// For marks only; AiFeedbackDiff keeps declining more-is-worse ladders.
export const meaningScores = (levelNames) => {
  const normalizedLevels = levelNames.map(normalize);
  const matches = normalizedLevels.map((normalized) =>
    normalized === null
      ? []
      : MARK_VOCABULARY.filter(([phrase]) => normalized.includes(` ${phrase} `))
  );
  const shared = new Set(
    levelNames.length < 2
      ? []
      : matches[0]
          .map(([phrase]) => phrase)
          .filter((phrase) =>
            matches.every((found) => found.some(([other]) => other === phrase))
          )
  );
  // Only when every level still has a word of its own: in "Not yet competent
  // | Competent" the shared word is all one level says.
  const ignored = matches.every((found) =>
    found.some(([phrase]) => !shared.has(phrase))
  )
    ? shared
    : new Set();
  return matches.map((found, k) => {
    // A label before a separator ("Green - I understand", "Level 4 - Thorough")
    // names the level; read it before the description that follows.
    const label = labelOf(levelNames[k]);
    const labelNormalized = label === null ? null : normalize(label);
    const fromLabel =
      labelNormalized === null
        ? []
        : found.filter(
            ([phrase]) =>
              !ignored.has(phrase) && labelNormalized.includes(` ${phrase} `)
          );
    const candidates =
      fromLabel.length > 0
        ? fromLabel
        : found.filter(([phrase]) => !ignored.has(phrase));
    const word =
      candidates.find(([phrase]) => !isModifierPhrase(phrase)) || candidates[0];
    if (!word) {
      return null;
    }
    const [phrase, score] = word;
    const strength = modified(
      fromLabel.length > 0 ? labelNormalized : normalizedLevels[k],
      phrase,
      score
    );
    return qualifiesMoreIsWorseNoun(levelNames[k], phrase)
      ? 100 - strength
      : strength;
  });
};

// Direction from numbered ("1..4", "Band 6..Band 1") or lettered ("A..D")
// names: the numbering itself orders the ladder.
export const positionalOrdering = (levelNames) => {
  if (levelNames.length < 2) {
    return null;
  }
  const numbers = levelNames.map(firstNumber);
  if (numbers.every((value) => value !== null)) {
    return guardedOrdering(levelNames, numbers, true);
  }
  const letters = levelNames.map(letterOrdinal);
  if (letters.every((value) => value !== null)) {
    return guardedOrdering(levelNames, letters, false);
  }
  return null;
};

// Direction from the quality vocabulary. It settles direction only; callers
// still rank by array position, keeping the author's order between rungs the
// vocabulary buckets together.
export const vocabularyOrdering = (levelNames) =>
  hasInvertedPolarityWording(levelNames)
    ? null
    : ordering(
        levelNames.map(qualityScore).filter((score) => score !== null),
        true
      );

export const isWorstFirstLadder = (levelNames) => {
  const positional = positionalOrdering(levelNames);
  return positional !== null ? positional : vocabularyOrdering(levelNames);
};
